// usePhysicianEmailStatement.ts
import { useMutation } from 'react-query' // v3
import type { AxiosError, AxiosInstance } from 'axios'
import { axiosInstance } from './axiosConfig'

export interface usePhysicianEmailStatementProps {
  onSuccess?: () => void
  onError?: (error: EmailStatementApiError) => void
  axios?: AxiosInstance
  /** Turn on retries if your backend allows re-triggers safely */
  retry?: boolean | number
}

export interface EmailStatementError {
  message?: string
  statementRequestCount?: number
  lastStatementRequestTime?: string
  canRequestStatement?: boolean
  nextAvailableTime?: string
}

export interface ValidationError {
  attribute: string
  errorMessage: string
}

export interface EmailStatementRequest {
  physicianId: string
  recaptchaToken?: string
}

/** Union of what the API might return on error */
export type EmailStatementApiError = AxiosError<
  | EmailStatementError
  | { errors?: ValidationError[]; message?: string }
  | string
>

/**
 * Triggers an email statement for the physician. Many such endpoints return 204/empty body,
 * so this function resolves to void.
 */
const requestPhysicianEmailStatement = async (
  payload: EmailStatementRequest,
  instance: AxiosInstance
): Promise<void> => {
  const id = payload.physicianId?.trim()
  if (!id) throw new Error('physicianId is required')
  
  console.log('[DEBUG] Sending email statement request:', {
    physicianId: id,
    recaptchaToken: payload.recaptchaToken?.substring(0, 20) + '...',
    tokenLength: payload.recaptchaToken?.length,
    endpoint: `/provider/${id}/email-statement`,
    baseURL: instance.defaults.baseURL
  })
  
  try {
    await instance.post(`/provider/${id}/email-statement`, {
      recaptchaToken: payload.recaptchaToken,
    })
    console.log('[DEBUG] Email statement request successful')
  } catch (error: any) {
    console.error('[DEBUG] Email statement request failed:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers
    })
    throw error
  }
}

export const usePhysicianEmailStatement = ({
  onSuccess,
  onError,
  axios = axiosInstance,
  retry = false,
}: usePhysicianEmailStatementProps = {}) => {
  const mutation = useMutation<void, EmailStatementApiError, EmailStatementRequest>(
    (payload) => requestPhysicianEmailStatement(payload, axios),
    {
      retry,
      onSuccess: () => {
        onSuccess?.()
      },
      onError: (error) => {
        onError?.(error)
      },
    }
  )

  return {
    requestEmailStatementAsync: mutation.mutateAsync,
    /** state */
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    /** helpers */
    reset: mutation.reset,
    status: mutation.status,
  }
}
